/**
 * Client-side Geoapify credit budget tracker.
 * Estimates daily credit usage and blocks API calls when limit is reached.
 * Resets at midnight (local time).
 *
 * Credit costs (from Geoapify pricing):
 * - Geocoding / Autocomplete: 1 credit each
 * - Routing: 1 + floor(routeKm/500) for routes >500km
 * - Static Maps: 1 + MAP_TILES/4 + MAP_MARKERS (2 markers = 2 credits)
 */

const STORAGE_KEY_PREFIX = "geoapify_credits_";
const DEFAULT_DAILY_LIMIT = 2500; // Stay under 3000 free tier with buffer

function getTodayKey(): string {
  return `${STORAGE_KEY_PREFIX}${new Date().toISOString().slice(0, 10)}`;
}

function getDailyLimit(): number {
  const env = import.meta.env.VITE_GEOAPIFY_DAILY_CREDIT_LIMIT;
  if (env && typeof env === "string") {
    const parsed = parseInt(env, 10);
    if (!isNaN(parsed) && parsed > 0) return parsed;
  }
  return DEFAULT_DAILY_LIMIT;
}

function getUsedToday(): number {
  try {
    const key = getTodayKey();
    const raw = localStorage.getItem(key);
    if (!raw) return 0;
    const n = parseInt(raw, 10);
    return isNaN(n) ? 0 : Math.max(0, n);
  } catch {
    return 0;
  }
}

function setUsedToday(credits: number): void {
  try {
    localStorage.setItem(getTodayKey(), String(Math.max(0, credits)));
  } catch {
    // localStorage full or disabled
  }
}

/**
 * Check if we can spend the given credits without exceeding the daily limit.
 */
export function canSpendCredits(estimatedCredits: number): boolean {
  const used = getUsedToday();
  const limit = getDailyLimit();
  return used + estimatedCredits <= limit;
}

/**
 * Record credits used. Call after a successful API request.
 */
export function recordCreditsUsed(credits: number): void {
  const used = getUsedToday();
  setUsedToday(used + credits);
}

/**
 * Estimated credits for Geocoding / Autocomplete (1 credit each).
 */
export const GEOCODING_CREDITS = 1;

/**
 * Estimated credits for Routing API.
 * 1 credit base + 1 per 500km for routes >500km.
 */
export function estimateRoutingCredits(routeKm: number): number {
  if (routeKm <= 500) return 1;
  return 1 + Math.floor(routeKm / 500);
}

/**
 * Estimated credits for Static Maps API.
 * 1 base + tiles/4 + 2 markers. 400x300 @2x = 800x600 ≈ 12 tiles → 3 credits.
 */
export function estimateStaticMapCredits(width = 400, height = 300, markers = 2): number {
  const tiles = Math.ceil(width / 256) * Math.ceil(height / 256);
  return 1 + Math.ceil(tiles / 4) + markers;
}

/**
 * Get current usage and limit for display.
 */
export function getCreditStatus(): { used: number; limit: number; remaining: number } {
  const used = getUsedToday();
  const limit = getDailyLimit();
  return { used, limit, remaining: Math.max(0, limit - used) };
}
